import axios, { CancelTokenSource } from 'axios';
import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import SaveActionButton from 'components/SaveActionButton';
import arrayMutators from "final-form-arrays";
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, Table } from 'semantic-ui-react';
import { getAllProcedureGroupsByTariffId } from 'services/procedureGroupServices';
import { addTariffTaxPoint, editTariffTaxPoint } from 'services/tariffTaxPointServices';
import { getAllTaxPointsForTariffTaxPoint } from 'services/taxPointServices';
import styled from 'styled-components';
import { AlternativeTaxPointGroup } from 'ts-types/api.enums';
import {
  ProcedureGroupDto,
  TariffDto,
  UpsertTariffTaxPointDto,
  UpsertTariffTaxPointSubmitDto,
  UpsertTaxPointDto
} from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';
import { composeValidators, mustBeNumber, mustBePositiveNumber, required } from 'utils/validatorUtils';

const ScrollableTableContainer = styled.div`
  height: 247px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34,36,38,.15);
  box-shadow: none;
  border-radius: .28571429rem;
  padding: 0 !important;

  & .ui.table {
    border: none;
  }

  & .ui.table thead tr:first-child > th {
      position: sticky !important;
      top: 0;
      z-index: 2;
      background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
      border-bottom: 1px solid rgba(34,36,38,.15)
  }
`;

interface ProcedureGroupTaxPoint {
  procedureGroup: ProcedureGroupDto,
  taxPoint?: UpsertTaxPointDto
}

interface InitialValues extends UpsertTariffTaxPointDto {
  procedureGroupTaxPoints: ProcedureGroupTaxPoint[]
}

interface Props extends MessageSourceProps {
  onCancel: () => void,
  onSave: () => void,
  tariffTaxPointId?: number,
  tariffTaxPoint: UpsertTariffTaxPointDto,
  activeTariff: TariffDto
}

interface State {
  errorMessage?: string,
  editTaxPoint: boolean,
  editTaxPointIndex?: number,
  taxPoints: UpsertTaxPointDto[],
  procedureGroups: ProcedureGroupDto[],
  alternativeTaxPointGroups: DropdownOption[],
  initialValues?: Partial<InitialValues>,
  procedureGroupTaxPointsCopy?: Partial<ProcedureGroupTaxPoint>,
  cancelTokenSource: CancelTokenSource
}

class UpsertTariffTaxPointForm extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const {activeTariff, tariffTaxPointId} = props;

    const cancelTokenSource = axios.CancelToken.source();

    const alternativeTaxPointGroups: DropdownOption[] =
        Object.values(AlternativeTaxPointGroup).map((value: string) => {
          return {
            key: value,
            text: value,
            // text: getMessage(`alternativeTaxPointGroup.${value.toLowerCase()}`),
            value: value
          }
        });

    this.state = {
      editTaxPoint: false,
      procedureGroups: [],
      taxPoints: [],
      alternativeTaxPointGroups,
      cancelTokenSource
    };

    getAllProcedureGroupsByTariffId(activeTariff.id, cancelTokenSource).then(procedureGroups => {
      if (tariffTaxPointId) {
        getAllTaxPointsForTariffTaxPoint(tariffTaxPointId, cancelTokenSource).then(taxPoints => {
          this.setState({
            taxPoints: taxPoints,
            procedureGroups: procedureGroups,
            initialValues: this.makeInitialValuesObject(taxPoints, procedureGroups)
          })
        }).catch(noop);
      } else {
        this.setState({
          procedureGroups: procedureGroups,
          initialValues: this.makeInitialValuesObject([], procedureGroups)
        })
      }
    }).catch(noop);
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  handleSubmit = async (values: Partial<InitialValues>) => {

    const {
      editTaxPoint,
      procedureGroupTaxPointsCopy,
      cancelTokenSource
    } = this.state;

    const {
      tariffTaxPointId,
      getMessage,
      onSave,
      activeTariff
    } = this.props;


    let taxPoints: UpsertTaxPointDto[] = [];

    values.procedureGroupTaxPoints!.forEach(x => {
      if (editTaxPoint && procedureGroupTaxPointsCopy) {
        if (x.procedureGroup.id === procedureGroupTaxPointsCopy.procedureGroup!.id) {
          x.taxPoint = procedureGroupTaxPointsCopy.taxPoint
        }
      }

      if (x.taxPoint) {
        if (!x.taxPoint.procedureGroupId) {
          x.taxPoint.procedureGroupId = x.procedureGroup.id;
        }
        taxPoints.push(x.taxPoint);
      }
    });

    let saveValues: UpsertTariffTaxPointSubmitDto = {
      tariffId: activeTariff.id,
      taxPoints: taxPoints,
      defaultPoints: values.defaultPoints!,
      fromDate: values.fromDate!,
      alternativeTaxPointGroup: values.alternativeTaxPointGroup
    };

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (tariffTaxPointId) {
      await editTariffTaxPoint(tariffTaxPointId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addTariffTaxPoint(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  makeInitialValuesObject = (taxPoints: UpsertTaxPointDto[], procedureGroups: ProcedureGroupDto[]) => {
    const {tariffTaxPoint} = this.props;

    let procedureGroupTaxPoints: ProcedureGroupTaxPoint[] = [];
    procedureGroups.forEach(procedureGroup => {
      const taxPoint = taxPoints.find(
          taxPoint => taxPoint.procedureGroupId === procedureGroup.id);
      procedureGroupTaxPoints.push({procedureGroup: procedureGroup, taxPoint: taxPoint});
    });
    let initalValues: Partial<InitialValues> = {
      ...tariffTaxPoint,
      procedureGroupTaxPoints: procedureGroupTaxPoints
    };
    return initalValues;
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {
    const {initialValues} = this.state;

    return (
        <FinalForm
            onSubmit={this.handleSubmit}
            initialValues={initialValues}
            subscription={{pristine: true, submitting: true}}
            render={this.renderForm}
            mutators={{
              ...arrayMutators
            }}
        />
    );
  }

  renderProcedureGroupsSegment = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {editTaxPoint, editTaxPointIndex} = this.state;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <Header as='h5'>
                {getMessage("tariffTaxPoint.procedureGroups")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <ScrollableTableContainer>
                <Table celled selectable size="small">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{getMessage('tariffTaxPoint.procedureGroups.description')}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage('tariffTaxPoint.procedureGroups.value')}</Table.HeaderCell>
                      <Table.HeaderCell textAlign={"center"}>{getMessage('actions')}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <FieldArray name="procedureGroupTaxPoints">
                      {({fields}) =>
                          fields.map((field, index) => {
                            const procedureGroupTaxPoint: ProcedureGroupTaxPoint = fields.value[index];
                            const procedureGroup = procedureGroupTaxPoint.procedureGroup;
                            const taxPoint = procedureGroupTaxPoint.taxPoint;

                            if (editTaxPoint && editTaxPointIndex === index) {

                              return (
                                  <Table.Row key={index}>
                                    <Table.Cell width={10}>
                                      {procedureGroup.description}
                                    </Table.Cell>
                                    <Table.Cell width={4}>
                                      <Field
                                          name={`${field}.taxPoint.value`}
                                          component={Input}
                                          validate={composeValidators(mustBeNumber, mustBePositiveNumber)}
                                          size='mini'
                                      />
                                    </Table.Cell>
                                    <Table.Cell width={2} textAlign={"center"}>
                                      <SaveActionButton hoverMessage={getMessage('button.save')}
                                                        onConfirm={() => this.saveTaxPoint(fields)}/>
                                      <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                                          onConfirm={() => this.cancelEditTaxPoint(fields, index)}/>
                                    </Table.Cell>
                                  </Table.Row>
                              )
                            } else {
                              return (
                                  <Table.Row key={index}>
                                    <Table.Cell width={10}>
                                      {procedureGroup.description}
                                    </Table.Cell>
                                    <Table.Cell width={4} textAlign={"right"}>
                                      {taxPoint ? taxPoint.value : ""}
                                    </Table.Cell>
                                    <Table.Cell width={2} textAlign={"center"}>
                                      <EditActionButton
                                          hoverMessage={getMessage('edit')}
                                          onConfirm={() => this.editTaxPoint(index, fields, procedureGroupTaxPoint)}
                                      />
                                      <DeleteActionButton
                                          hoverMessage={getMessage("msgDelete")}
                                          onConfirm={() => this.deleteTaxPoint(fields, index)}
                                      />
                                    </Table.Cell>
                                  </Table.Row>
                              )
                            }
                          })
                      }
                    </FieldArray>
                  </Table.Body>
                </Table>
              </ScrollableTableContainer>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  };

  renderTariffTaxPointSegment = (): React.ReactNode => {

    const {alternativeTaxPointGroups} = this.state;

    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={4} verticalAlign="middle">
              {getMessage("tariffTaxPoint.defaultPoints")}:
            </Grid.Column>
            <Grid.Column width={12}>
              <Field
                  fluid
                  name="defaultPoints"
                  component={Input}
                  validate={composeValidators(mustBeNumber, mustBePositiveNumber)}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4} verticalAlign="middle">
              {getMessage("tariffTaxPoint.fromDate")}:
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="fromDate"
                  component={DatePicker}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4} verticalAlign="middle">
              {getMessage("alternativeTaxPointGroup")}:
            </Grid.Column>
            <Grid.Column width={12}>
              <Field
                  name="alternativeTaxPointGroup"
                  component={Select}
                  options={alternativeTaxPointGroups}
                  clearable
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  };

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {
    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                {this.renderTariffTaxPointSegment()}
                {this.renderProcedureGroupsSegment()}
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  saveTaxPoint = (fields: any) => {
    if (fields.valid) {
      this.setState({
        editTaxPoint: false,
        editTaxPointIndex: undefined,
        procedureGroupTaxPointsCopy: undefined
      })
    }
  };

  editTaxPoint = (index: number, fields: any, procedureGroupTaxPoint: ProcedureGroupTaxPoint): void => {
    const {procedureGroupTaxPointsCopy, editTaxPointIndex} = this.state;

    if (procedureGroupTaxPointsCopy) {
      fields.remove(editTaxPointIndex);
      fields.insert(editTaxPointIndex, procedureGroupTaxPointsCopy);
    }

    this.setState({
      editTaxPoint: true,
      editTaxPointIndex: index,
      procedureGroupTaxPointsCopy: procedureGroupTaxPoint
    })

  };

  cancelEditTaxPoint = (fields: any, index: number) => {
    const {procedureGroupTaxPointsCopy} = this.state;

    fields.remove(index);
    fields.insert(index, procedureGroupTaxPointsCopy);

    this.setState({
      editTaxPoint: false,
      editTaxPointIndex: undefined,
      procedureGroupTaxPointsCopy: undefined
    });


  };

  deleteTaxPoint = (fields: any, index: number) => {
    let procedureGroupTaxPoint = fields.value[index];
    procedureGroupTaxPoint.taxPoint = undefined;

    fields.remove(index);
    fields.insert(index, procedureGroupTaxPoint);
  }
}

export default withMessages(UpsertTariffTaxPointForm);
